import React, { ChangeEvent, useState } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import { useQueryClient } from "@tanstack/react-query";
import addDays from "date-fns/addDays";

import Button from "@material-ui/core/Button";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import FormField from "components/FormField";
import { FIELD_TYPE } from "components/FormField/constants";
import ActionPopup from "components/ActionPopup";
import { EMPTY_STRING } from "common/constants";
import { setIsAddCustomRiskPopupOpen } from "store/actions/vetHorseDetailsPage";
import { useRacehorse360Api } from "hooks/api";
import { LIST_HORSE_RISK_TOTAL_RESULTS_QUERY_KEY } from "../RiskList/RiskList";

import useStyles from "./styles";

interface IProps {
  horse: racehorse360.IHorse;
}

const ARRAY_SIZE = 10;

const AddCustomRiskPopup = (props: IProps) => {
  const { horse } = props;
  const defaultSelectedDate = addDays(new Date(), 1);

  const classes = useStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [expirationDate, setExpirationDate] = useState<Date>(null);
  const [riskTitle, setRiskTitle] = useState<string>(EMPTY_STRING);
  const [riskDescription, setRiskDescription] = useState<string>(EMPTY_STRING);
  const [riskScore, setRiskScore] = useState<string>(null);

  const { useCreateHorseRisk } = useRacehorse360Api();

  const { mutateAsync: createHorseRisk } = useCreateHorseRisk();

  const handleCloseAddCustomRiskPopup = () => {
    dispatch(setIsAddCustomRiskPopupOpen(false));

    setRiskTitle(EMPTY_STRING);
    setRiskDescription(EMPTY_STRING);
    setExpirationDate(null);
    setRiskScore(null);
  };

  const handleChangeRiskTitle = (event: ChangeEvent<HTMLInputElement>) => {
    setRiskTitle(event.target.value);
  };

  const handleChangeRiskDescription = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setRiskDescription(event.target.value);
  };

  const handleChangeExpirationDate = (date: Date) => {
    setExpirationDate(date);
  };

  const handleChangeRiskScore = (newValue: string) => {
    setRiskScore(prev => (prev === newValue ? null : newValue));
  };

  const handleAddCustomRisk = async () => {
    if (isSubmitButtonAvailable) {
      return createHorseRisk({
        horseId: horse?.id,
        title: riskTitle,
        description: riskDescription,
        score: Number(riskScore),
        expirationDate: expirationDate?.toISOString()
      }).then(() => {
        handleCloseAddCustomRiskPopup();
        queryClient.invalidateQueries({
          queryKey: [LIST_HORSE_RISK_TOTAL_RESULTS_QUERY_KEY]
        });
      });
    }
  };

  const isSubmitButtonAvailable = [riskTitle, riskDescription, riskScore].every(
    Boolean
  );

  const buttons = Array.from({ length: ARRAY_SIZE }, (_, index) => {
    const value = String(index + 1);

    return (
      <Button
        key={index}
        className={clsx(classes.riskScoreButton, {
          selected: value === riskScore
        })}
        variant={"outlined"}
        value={value}
        onClick={() => handleChangeRiskScore(value)}
      >
        {index + 1}
      </Button>
    );
  });

  return (
    <ActionPopup
      className={classes.addCustomRiskPopup}
      open={true}
      onClose={handleCloseAddCustomRiskPopup}
      onSubmit={handleAddCustomRisk}
      onCancel={handleCloseAddCustomRiskPopup}
      title={"Add a Risk Factor"}
      cancelButtonText={"Close"}
      submitButtonText={"Add New Risk Factor"}
      notification={`${riskTitle} has been added to ${horse?.name}`}
      disabledAction={!isSubmitButtonAvailable}
      scrollable={true}
    >
      <div className={classes.fieldGroupTitle}>Risk Details</div>
      <div className={classes.riskDetailsContainer}>
        <FormField
          className={classes.formField}
          field={{
            maxLength: 50,
            name: "title",
            label: EMPTY_STRING,
            type: FIELD_TYPE.TEXT,
            placeholder: "Title*"
          }}
          moduleName={EMPTY_STRING}
          value={riskTitle}
          onChange={handleChangeRiskTitle}
          onChangeCustom={() => handleChangeRiskTitle}
        />
        <FormField
          className={classes.formField}
          field={{
            maxLength: 100,
            name: "description",
            label: EMPTY_STRING,
            type: FIELD_TYPE.TEXT,
            placeholder: "Description*"
          }}
          moduleName={EMPTY_STRING}
          value={riskDescription}
          onChange={handleChangeRiskDescription}
          onChangeCustom={() => handleChangeRiskDescription}
        />
        <FormField
          className={classes.formField}
          field={{
            name: "expiration date",
            label: EMPTY_STRING,
            type: FIELD_TYPE.DATEPICKER,
            placeholder: "Expiration Date (Optional)",
            min: new Date(),
            initialFocusedDate: defaultSelectedDate
          }}
          moduleName={EMPTY_STRING}
          value={expirationDate}
          onChange={handleChangeExpirationDate}
          onChangeCustom={() => handleChangeExpirationDate}
        />
      </div>

      <div className={classes.fieldGroupTitle}>Risk Score</div>
      <div className={classes.riskScoreContainer}>{buttons}</div>
    </ActionPopup>
  );
};

export default React.memo(AddCustomRiskPopup);
